import { z } from 'zod';

import { accreditationExtraDataFieldSchema } from 'entities/AccreditationExtraDataField';
import { accreditationStageSchema, companySchema } from 'entities/Company';

export const accreditationPhaseSchema = z.enum(['profile', 'extra_data', 'contract']);

export type AccreditationPhase = z.infer<typeof accreditationPhaseSchema>;

const accreditationRejectionReasonSchema = z.object({
  id: z.number(),
  text: z.string(),
});

export const accreditationRejectionSchema = z
  .object({
    id: z.number(),
    phase: accreditationPhaseSchema,
    reason: accreditationRejectionReasonSchema.nullable().optional(),
    comment: z.string().nullable().optional(),
    created_at: z.string(),
  })
  .transform((raw) => ({
    id: raw.id,
    phase: raw.phase,
    reasonText: raw.reason?.text ?? null,
    comment: raw.comment ?? null,
    createdAt: raw.created_at,
  }));

export type AccreditationRejection = z.infer<typeof accreditationRejectionSchema>;

export const accreditationContractSchema = z
  .object({
    contract_number: z.string().nullable(),
    generated_contract_url: z.string().nullable(),
    signed_contract_id: z.number().nullable(),
    document_pending: z.boolean(),
    read_only: z.boolean(),
  })
  .transform((raw) => ({
    contractNumber: raw.contract_number,
    generatedContractUrl: raw.generated_contract_url,
    signedContractId: raw.signed_contract_id,
    documentPending: raw.document_pending,
    readOnly: raw.read_only,
  }));

export type AccreditationContract = z.infer<typeof accreditationContractSchema>;

export const accreditationSnapshotSchema = z
  .object({
    stage: accreditationStageSchema,
    phase: accreditationPhaseSchema,
    active_rejection: accreditationRejectionSchema.nullable(),
    extra_fields: z.array(accreditationExtraDataFieldSchema).nullable(),
    contract: accreditationContractSchema.nullable(),
    available_actions: z.array(z.string()),
  })
  .transform((raw) => ({
    stage: raw.stage,
    phase: raw.phase,
    activeRejection: raw.active_rejection,
    extraFields: raw.extra_fields,
    contract: raw.contract,
    availableActions: raw.available_actions,
  }));

export type AccreditationSnapshot = z.infer<typeof accreditationSnapshotSchema>;

export const accreditationPageResponseSchema = z.object({
  company: companySchema,
  accreditation: accreditationSnapshotSchema,
});

export type AccreditationPageResponse = z.infer<typeof accreditationPageResponseSchema>;

export const accreditationSubmitResultSchema = z
  .object({
    company_id: z.number(),
    stage: accreditationStageSchema,
    phase: accreditationPhaseSchema,
    available_actions: z.array(z.string()),
  })
  .transform((raw) => ({
    companyId: raw.company_id,
    stage: raw.stage,
    phase: raw.phase,
    availableActions: raw.available_actions,
  }));

export type AccreditationSubmitResult = z.infer<typeof accreditationSubmitResultSchema>;

export type AccreditationExtraDataSubmitPayload = {
  action: 'submit_extra_data';
  fields: Record<string, string | boolean | number>;
};

const mergeRejectionReasonAndComment = ({
  reasonText,
  comment,
}: Pick<AccreditationRejection, 'reasonText' | 'comment'>): string | null => {
  if (reasonText && comment) {
    return `${reasonText}. ${comment}`;
  }

  return reasonText || comment || null;
};

export const getAccreditationRejectionComment = (rejection: AccreditationRejection | null): string | null => {
  if (!rejection) {
    return null;
  }

  return mergeRejectionReasonAndComment(rejection);
};
